import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  QrCode,
  Calendar,
  BarChart3,
  Bot,
  Settings,
  Smartphone,
  LucideIcon
} from 'lucide-react';
import FeatureCard from '../../../components/ui/FeatureCard';

// Hook pour détecter la taille d'écran
const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1024,
    height: typeof window !== 'undefined' ? window.innerHeight : 768,
  });

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
      handleResize();
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  return windowSize;
};

interface Feature {
  icon: LucideIcon;
  title: string;
  description: string;
}

const features: Feature[] = [
  {
    icon: QrCode,
    title: "Menu QR Code",
    description: "Créez et gérez votre menu digital avec QR code. Vos clients scannent et consultent votre carte directement sur leur smartphone."
  },
  {
    icon: Calendar,
    title: "Gestion des Réservations",
    description: "Système de réservation intégré avec notifications automatiques. Optimisez votre planning et réduisez les no-shows."
  },
  {
    icon: BarChart3,
    title: "Commandes Centralisées",
    description: "Centralisez toutes vos commandes Uber Eats, Deliveroo et autres plateformes en un seul endroit pour une gestion simplifiée."
  },
  {
    icon: Bot,
    title: "Intelligence Artificielle",
    description: "IA intégrée qui génère automatiquement vos posts sur les réseaux sociaux, répond aux réservations et optimise votre communication digitale."
  },
  {
    icon: Settings,
    title: "Personnalisation Facile",
    description: "Interface intuitive pour personnaliser votre menu, vos horaires et vos informations. Aucune compétence technique requise."
  },
  {
    icon: Smartphone,
    title: "100% Responsive",
    description: "Navigation optimisée sur tous les appareils. Interface fluide et intuitive sur mobile, tablette et desktop."
  }
];

const Features: React.FC = () => {
  const { width } = useWindowSize();
  const isMobile = width < 768;
  const isTablet = width >= 768 && width < 1024;

  return (
    <section id="features" className="py-12 md:py-16 bg-white relative">
      <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 relative w-full">
        {/* Header minimaliste style Apple avec touche orange */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ 
            duration: 0.8, 
            ease: [0.25, 0.46, 0.45, 0.94] 
          }}
          viewport={{ once: true, margin: "-100px" }}
          className="text-center mb-8 md:mb-12"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light text-gray-900 mb-3 tracking-tight leading-tight">
            Tout ce dont votre restaurant
            <span className="block font-extralight text-gray-700">
              a besoin
            </span>
          </h2>
          
          <div className="w-16 h-1 bg-gradient-to-r from-orange-400 to-orange-500 mx-auto mb-4"></div>
          
          <p className="text-base sm:text-lg text-gray-500 max-w-2xl mx-auto leading-relaxed font-light">
            Une solution complète et moderne pour digitaliser votre établissement 
            et offrir une expérience exceptionnelle à vos clients
          </p>
        </motion.div>

        {/* Features Grid compact */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {features.map((feature, index) => (
            <FeatureCard
              key={index}
              icon={feature.icon}
              title={feature.title}
              description={feature.description}
              index={index}
              isMobile={isMobile}
              isTablet={isTablet}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;